import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ArrowRight } from "lucide-react";
import { useCountry } from "../context/CountryContext";
import { api } from "../lib/api";
import { ActionHub, CoverageResults } from "../components/ActionHub";
import { DomainResults } from "../components/DomainResults";
import { ComingSoon } from "../components/ComingSoon";
import { HeroSlider } from "../components/home/HeroSlider";
import { Deals, NetworkMarquee, ProductTabs, WhyGrid } from "../components/home/Sections";
import { CountUp } from "../components/CountUp";

const SectionTitle = ({ eyebrow, title, sub }) => (
  <div className="max-w-2xl">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
    <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">{title}</h2>
    {sub && <p className="mt-2 text-base text-muted-foreground">{sub}</p>}
  </div>
);

export default function Home() {
  const { country, countries } = useCountry();
  const [pkgs, setPkgs] = useState([]);
  const [cov, setCov] = useState(null);
  const [dom, setDom] = useState(null);

  useEffect(() => {
    if (!country) return;
    setCov(null); setDom(null);
    api.get("/packages", { params: { country: country.code } }).then((r) => setPkgs(r.data));
  }, [country]);

  const liveCount = countries.filter((c) => c.live).length;
  const networks = new Set(pkgs.map((p) => p.network)).size;

  return (
    <div data-testid="home-page">
      <HeroSlider />
      <div className="relative z-10 mx-auto -mt-20 max-w-5xl px-4 sm:px-6">
        <div className="fade-up rounded-3xl border bg-white p-4 shadow-[0_30px_60px_-30px_rgba(27,16,51,0.35)] sm:p-6" data-testid="hero-action-hub">
          <ActionHub onCoverage={(d) => { setDom(null); setCov(d); }} onDomains={(d) => { setCov(null); setDom(d); }} />
        </div>
      </div>

      {(cov || dom) && (
        <section className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8" data-testid="hero-results">
          {cov && <CoverageResults data={cov} />}
          {dom && <div className="mx-auto mt-6 max-w-3xl"><DomainResults data={dom} /></div>}
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <SectionTitle eyebrow="Deals & promos" title={`Hot right now in ${country?.name || "Africa"}`} />
        </div>
        <div className="mt-8">{country && !country.live ? <ComingSoon country={country} /> : <Deals pkgs={pkgs} country={country} />}</div>
      </section>

      {pkgs.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-6 lg:px-8">
          <SectionTitle eyebrow="Choose your connection" title="Packages you'll love" sub={`Prices in ${country?.currency}, free setup, no contracts.`} />
          <div className="mt-8"><ProductTabs pkgs={pkgs} /></div>
        </section>
      )}

      <section className="mt-20 bg-[#FEF6F8] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 text-center sm:grid-cols-4" data-testid="stats-band">
            {[[54, "", "African countries"], [liveCount, "", "countries live today"], [pkgs.length, "", `packages in ${country?.name || ""}`], [networks, "", "local networks"]].map(([n, s, l], i) => (
              <div key={l}>
                <p className="font-display text-5xl font-black text-primary"><CountUp to={n} suffix={s} testid={`stat-count-${i}`} /></p>
                <p className="mt-1 text-sm font-semibold text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
          {pkgs.length > 0 && (
            <div className="mt-12">
              <p className="mb-4 text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Running on trusted local networks</p>
              <NetworkMarquee pkgs={pkgs} />
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionTitle eyebrow="Why HostAfricair" title="We just do internet better." />
        <div className="mt-10"><WhyGrid /></div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="relative flex flex-col items-start justify-between gap-6 overflow-hidden rounded-[2rem] bg-ink p-8 text-white sm:p-12 md:flex-row md:items-center">
          <div className="blob absolute -right-20 -top-20 h-72 w-72 bg-primary/40" />
          <div className="blob absolute -bottom-24 left-1/3 h-60 w-60 bg-[#0FB5A5]/30" style={{ animationDelay: "-4s" }} />
          <div className="relative">
            <Building2 className="h-8 w-8 text-[#FFC23D]" />
            <h2 className="mt-3 text-3xl font-extrabold sm:text-4xl">Are you an ISP or wholesaler?</h2>
            <p className="mt-2 max-w-xl text-white/75">List your packages on HostAfricair and reach customers in your country. Connect by portal or API.</p>
          </div>
          <Link to="/partners" data-testid="home-become-supplier-btn" className="group relative flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
            Become a supplier <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
